/**
 * API fetch wrapper for intranet function endpoints (/api/*).
 *
 * Auth: SWA session cookie sent automatically via credentials: 'include'.
 * No Bearer token needed — the SWA platform validates the session and
 * injects x-ms-client-principal into every function request.
 */

import { classifyApiError } from './classifyApiError'
import { embeddedErrorMessage } from './embeddedError'

// ── Error ─────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Machine-readable code from the function's JSON body, e.g. 'AI_NOT_CONFIGURED'. */
    public readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** The SWA session cookie expired (or the request otherwise hit SWA's own
 * 401/HTML edge response) — the SitzungAbgelaufenDialog of the auth provider
 * (AuthProvider / StaffShell, §6.59) is the intended handling, not a retry. */
export class SessionExpiredError extends ApiError {
  constructor(status: number, message = 'Sitzung abgelaufen — bitte neu anmelden.', code = 'session_expired') {
    super(status, message, code)
    this.name = 'SessionExpiredError'
  }
}

// ── Fetch wrapper ─────────────────────────────────────────────────────────────

/** §6.59 — window-Ereignis bei jedem 401; Auth-Provider hoeren darauf. */
export const SITZUNG_ABGELAUFEN_EVENT = 'mc:sitzung-abgelaufen'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export interface ApiFetchOptions {
  /**
   * A 200 body that carries an `error` is normally thrown as ApiError(500). With this flag the body of a FAILED AI
   * job (`{ job_id, status: 'failed', error, error_code }` from GET ai-jobs/{id}) comes back as data instead, so the
   * caller can read `status` and `error_code`. Opt-in — useAiJob only; every other caller keeps the throw.
   */
  returnErrorBody?: boolean
}

export async function apiFetch<T>(path: string, init?: RequestInit, options?: ApiFetchOptions): Promise<T> {
  const hasBody = init?.body != null
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    // When calling localhost:7071 from localhost:5173, 'include' can cause CORS issues
    // if the backend isn't configured for it. But it's required for production SWA.
    credentials: import.meta.env.DEV ? 'omit' : 'include',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (!res.ok) {
    const bodyText = await res.text().catch(() => '')
    const classified = classifyApiError({
      path,
      status: res.status,
      contentType: res.headers.get('content-type'),
      bodyText,
    })
    // §6.59 (PR #681): JEDER 401 meldet das Ereignis; ob daraus ein Dialog wird,
    // entscheidet der jeweilige Auth-Provider ueber `aktiv` (auf der Anmeldeseite
    // und im Mitarbeiterportal ist ein 401 ohne Sitzung normal). Die HTML-Seite
    // von SWA (session_expired) zaehlt ebenfalls, auch wenn ihr Status kein 401 ist.
    if (typeof window !== 'undefined' && (res.status === 401 || classified.kind === 'session_expired')) {
      window.dispatchEvent(new CustomEvent(SITZUNG_ABGELAUFEN_EVENT, { detail: { path } }))
    }
    if (classified.kind === 'session_expired') {
      throw new SessionExpiredError(res.status, classified.message, classified.code)
    }
    throw new ApiError(res.status, classified.message, classified.code)
  }

  // Handle our special 200 response with embedded errors (from backend debugging)
  const json = await res.json().catch(() => null)
  const embedded = embeddedErrorMessage(json, options?.returnErrorBody)
  if (embedded) throw new ApiError(500, embedded)

  return json as T
}

export const api = {
  get:    <T>(path: string)                => apiFetch<T>(path),
  post:   <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST',   body: JSON.stringify(body) }),
  put:    <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PUT',    body: JSON.stringify(body) }),
  patch:  <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PATCH',  body: JSON.stringify(body) }),
  delete: <T>(path: string)                => apiFetch<T>(path, { method: 'DELETE'  }),
}
